import {
  CheckCircle2,
  Info,
  Navigation,
  TrafficCone,
  TriangleAlert,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import type { BadgeTone } from '@/components/ui/Badge';
import type {
  DriverStatus,
  RouteStatus,
  RouteTone,
  TransportAlertKind,
  TransportStudentStatus,
  TripKind,
  TripStatus,
  VehicleStatus,
} from '@/types/transport';

/**
 * How the transport section reads its own statuses.
 *
 * One table per set, here rather than inline in the screens, so a vehicle under
 * maintenance is the same word and the same colour on the dashboard, in the
 * fleet list and on the tracking board (CLAUDE.md §3).
 *
 * The tones are `Badge`'s own — the product's `ok` / `warn` / `crit` / `info`
 * family, which is deliberately *not* re-tinted per console. A delayed bus has
 * to mean the same thing here as an unpaid fee does on the fee counter.
 */

export const TRIP_STATUS: Record<TripStatus, { label: string; tone: BadgeTone }> = {
  ON_ROUTE: { label: 'On route', tone: 'success' },
  SCHEDULED: { label: 'Scheduled', tone: 'info' },
  COMPLETED: { label: 'Completed', tone: 'neutral' },
  DELAYED: { label: 'Delayed', tone: 'warning' },
  NOT_STARTED: { label: 'Not started', tone: 'critical' },
};

export const TRIP_KIND_LABEL: Record<TripKind, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

export const VEHICLE_STATUS: Record<VehicleStatus, { label: string; tone: BadgeTone }> = {
  ON_ROUTE: { label: 'On route', tone: 'success' },
  MAINTENANCE: { label: 'Maintenance', tone: 'warning' },
  INACTIVE: { label: 'Inactive', tone: 'critical' },
};

export const DRIVER_STATUS: Record<DriverStatus, { label: string; tone: BadgeTone }> = {
  ON_DUTY: { label: 'On duty', tone: 'success' },
  ON_LEAVE: { label: 'On leave', tone: 'warning' },
  INACTIVE: { label: 'Inactive', tone: 'critical' },
};

export const STUDENT_TRANSPORT_STATUS: Record<
  TransportStudentStatus,
  { label: string; tone: BadgeTone }
> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  // Neutral, not critical: a child who walks to school is not a problem to be
  // fixed. The tab lists them so the roll can be checked, not so they stand out.
  NOT_USING: { label: 'Not using', tone: 'neutral' },
};

export const ROUTE_STATUS: Record<RouteStatus, { label: string; tone: BadgeTone }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  SUSPENDED: { label: 'Suspended', tone: 'critical' },
};

/**
 * How each kind of notification is drawn.
 *
 * Here rather than in one of the rails, because two of them draw the same
 * alerts — the dashboard's feed and the tracking board's — and a traffic warning
 * that were amber on one screen and rose on the other would stop being read as
 * a warning at all (CLAUDE.md §3).
 *
 * The tones are the product's `ok` / `warn` / `crit` / `info` family, which is
 * deliberately not re-tinted per console.
 */
export const ALERT_STYLE: Record<TransportAlertKind, { icon: LucideIcon; tile: string }> = {
  DELAY: { icon: TriangleAlert, tile: 'bg-warn-bg text-warn' },
  INFO: { icon: Info, tile: 'bg-info-bg text-info' },
  SUCCESS: { icon: CheckCircle2, tile: 'bg-ok-bg text-ok' },
  MAINTENANCE: { icon: Wrench, tile: 'bg-crit-bg text-crit' },
  TRAFFIC: { icon: TrafficCone, tile: 'bg-warn-bg text-warn' },
  // A bus off its route is not a delay to be waited out — somebody has to ring
  // the driver — so it carries the same weight as a breakdown.
  DEVIATION: { icon: Navigation, tile: 'bg-crit-bg text-crit' },
};

/**
 * The five colours the map tells its lines apart by.
 *
 * Literal hues rather than theme tokens, and on purpose: these are a *legend*,
 * not a status. Four lines crossing one another have to stay distinguishable
 * from each other, which `--accent` — one colour — cannot do. They are the only
 * place in this section that names a hue.
 *
 * `stroke` paints the line on the map, `dot` the marker beside it in the list,
 * and `soft` the tile a route's letter sits in.
 */
export const ROUTE_TONE: Record<RouteTone, { stroke: string; dot: string; soft: string }> = {
  sky: {
    stroke: 'stroke-sky-500',
    dot: 'bg-sky-500',
    soft: 'bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-300',
  },
  emerald: {
    stroke: 'stroke-emerald-500',
    dot: 'bg-emerald-500',
    soft: 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300',
  },
  violet: {
    stroke: 'stroke-violet-500',
    dot: 'bg-violet-500',
    soft: 'bg-violet-50 dark:bg-violet-950 text-violet-700 dark:text-violet-300',
  },
  amber: {
    stroke: 'stroke-amber-500',
    dot: 'bg-amber-500',
    soft: 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300',
  },
  rose: {
    stroke: 'stroke-rose-500',
    dot: 'bg-rose-500',
    soft: 'bg-rose-50 dark:bg-rose-950 text-rose-700 dark:text-rose-300',
  },
};

/** `2026-11-25` → `25 Nov 2026`, the way an Indian office writes a date. */
export function toDateLabel(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Whole days between today and a date, negative once it is past.
 *
 * Counted from midnight at both ends rather than from the moment of asking, so
 * "90 days left" does not become 89 because the page was opened after lunch.
 */
export function daysUntil(iso: string, today = new Date()): number {
  const target = new Date(`${iso}T00:00:00`);
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - from.getTime()) / 86_400_000);
}

/**
 * How a date reads against today — "90 days left", "5 days ago", "Today".
 *
 * One helper for both directions because the two are the same question asked of
 * a fitness certificate and of a last service, and two of them would drift.
 */
export function toRelativeDayLabel(iso: string, today = new Date()): string {
  const days = daysUntil(iso, today);
  if (days === 0) return 'Today';
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} left`;
  const ago = Math.abs(days);
  return `${ago} day${ago === 1 ? '' : 's'} ago`;
}

/**
 * How urgently a paper needs attention: expired, close, or fine.
 *
 * The thresholds live here rather than in the screen, so the vehicle card and
 * the vehicle list cannot disagree about which certificate is a problem.
 */
export function expiryTone(iso: string, today = new Date()): 'ok' | 'warn' | 'crit' {
  const days = daysUntil(iso, today);
  if (days < 0) return 'crit';
  return days <= 30 ? 'warn' : 'ok';
}

/**
 * How a last service reads: fine, or long enough ago to look at.
 *
 * The opposite question from `expiryTone` and so a helper of its own — a date
 * in the past is *normal* here and alarming there, and one function answering
 * both would have to be told which it was being asked, every time.
 */
export function serviceTone(iso: string, today = new Date()): 'ok' | 'warn' {
  return daysUntil(iso, today) < -SERVICE_INTERVAL_DAYS ? 'warn' : 'ok';
}

/** How long a vehicle may go between services before the screen says so. */
const SERVICE_INTERVAL_DAYS = 90;

/** `07:15` → `07:15 AM`. The section prints clock times the way an office reads them. */
export function toClockLabel(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}\u00A0${suffix}`;
}
